import React, { useEffect, useState } from 'react';
import {
  IconChevronLeft,
  IconChevronRight,
  IconEdit,
  IconX,
  IconUsers,
  IconUserPlus,
  IconAlertCircle,
} from '@tabler/icons-react';
import { Search, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router';
import { toast } from 'react-toastify';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useDebounce } from 'use-debounce';

// UI Components
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../../../components/ui/tabs';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import { Skeleton } from '../../../../components/ui/skeleton';
import { Alert, AlertDescription } from '../../../../components/ui/alert';
// Services
import superAdminApiService from '../../../../services/super-admin-api.service';

function Organizations() {
  const [organizations, setOrganizations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [totalOrganizations, setTotalOrganizations] = useState(0);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [bypassAction, setBypassAction] = useState(null);

  // Special Users Modal states
  const [showSpecialUsersModal, setShowSpecialUsersModal] = useState(false);
  const [specialUsers, setSpecialUsers] = useState([]);

  const [originalSpecialUsers, setOriginalSpecialUsers] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  const [loadingStates, setLoadingStates] = useState({
    search: false,
    initialLoad: true,
    processingByPassAction: false,
    loadingSpecialUsers: false,
    savingSpecialUsers: false,
  });

  // Calculate pagination info
  const startItem =
    organizations.length > 0 ? (currentPage - 1) * limit + 1 : 0;
  const endItem =
    organizations.length > 0 ? startItem + organizations.length - 1 : 0;

  const fetchOrganizations = async (
    search = searchTerm,
    page = currentPage,
    pageLimit = limit,
  ) => {
    try {
      setLoadingStates((prev) => ({ ...prev, search: true }));
      const organizations = await superAdminApiService.listOrganizationUsers(
        pageLimit,
        search,
        page,
        1,
      );
      setOrganizations(organizations.results);
      setTotalOrganizations(organizations.total_org);
      setTotalPages(Math.ceil(organizations.total_org / pageLimit));
    } catch (error) {
      console.error(error);
      toast.error('Failed to load organizations. Please try again.');
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        search: false,
        initialLoad: false,
      }));
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchOrganizations(searchTerm, 1, limit);
  };

  const handleClearSearch = () => {
    if (loadingStates.search) return;
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleBypassClick = (org, newValue) => {
    setSelectedOrg(org);
    setBypassAction(newValue);
    setShowModal(true);
  };

  const handleConfirmBypass = async () => {
    try {
      setLoadingStates((prev) => ({ ...prev, processingByPassAction: true }));

      await superAdminApiService.setForceAllow(selectedOrg.organization_id, bypassAction);

      const updatedOrgs = organizations.map((org) =>
        org.user_name === selectedOrg.user_name
          ? { ...org, sub_force_allow: bypassAction }
          : org,
      );
      setOrganizations(updatedOrgs);
      toast.success('Subscription bypass status updated successfully.');
      setShowModal(false);
    } catch (error) {
      toast.error(
        'Failed to update subscription bypass status. Please try again or contact support if the issue persists.',
      );
    } finally {
      setLoadingStates((prev) => ({ ...prev, processingByPassAction: false }));
    }
  };

  const fetchSpecialUsers = async () => {
    try {
      setHasChanges(false);
      setLoadingStates((prev) => ({ ...prev, loadingSpecialUsers: true }));
      const specialUsers = await superAdminApiService.getSpecialOrganizations();
      setOriginalSpecialUsers(specialUsers?.emails || []);
      setSpecialUsers(specialUsers?.emails || []);
    } catch (error) {
      toast.error('Failed to load special organizations. Please try again.');
    } finally {
      setLoadingStates((prev) => ({ ...prev, loadingSpecialUsers: false }));
    }
  };

  const openSpecialUsersModal = async () => {
    setShowSpecialUsersModal(true);
    await fetchSpecialUsers();
  };

  const handleRemoveSpecialUser = (email) => {
    setSpecialUsers((prevUsers) => {
      const newUsers = prevUsers.filter((e) => e !== email);
      checkForChanges(newUsers);
      return newUsers;
    });
  };

  const handleSaveSpecialUsers = async () => {
    try {
      setLoadingStates((prev) => ({ ...prev, savingSpecialUsers: true }));
      await superAdminApiService.updateSpecialOrganizations(specialUsers);
      toast.success('Special organizations updated successfully');
      setShowSpecialUsersModal(false);
    } catch (error) {
      console.log(error);

      toast.error('Failed to update special organizations. Please try again.');
    } finally {
      setLoadingStates((prev) => ({ ...prev, savingSpecialUsers: false }));
    }
  };

  const checkForChanges = (currentUsers) => {
    // Different length means something was added or removed
    if (currentUsers.length !== originalSpecialUsers.length) {
      setHasChanges(true);
      return;
    }

    // Same length but might have different items
    const originalSet = new Set(originalSpecialUsers);

    // Check if any current user is not in the original set
    for (const email of currentUsers) {
      if (!originalSet.has(email)) {
        setHasChanges(true);
        return;
      }
    }

    // No changes detected
    setHasChanges(false);
  };

  // Validation schema for adding special organizations
  const addSpecialUserSchema = Yup.object().shape({
    email: Yup.string()
      .email('Invalid email address')
      .test(
        'unique-email',
        'This email is already in the list',
        (value) => !specialUsers.includes(value),
      ),
  });

  const handleAddSpecialUser = (values, { resetForm }) => {
    setSpecialUsers((prevUsers) => {
      const newUsers = [...prevUsers, values.email];
      checkForChanges(newUsers);
      return newUsers;
    });
    resetForm();
  };

  // Reset to page 1 when search term changes (while on a page > 1)
  const isFirstRender = React.useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (currentPage !== 1) {
      setCurrentPage(1); // triggers the fetch effect below
    } else {
      fetchOrganizations(debouncedSearchTerm, 1, limit);
    }
  }, [debouncedSearchTerm]);

  // Fetch on mount and when pagination/limit changes
  useEffect(() => {
    fetchOrganizations(debouncedSearchTerm, currentPage, limit);
  }, [currentPage, limit]);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-foreground">Organizations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage and view all organizations in the system
          </p>
        </div>

        {/* Search and Actions */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" 
              size={18} 
            />
            <input
              type="text"
              placeholder="Search organizations..."
              value={searchTerm}
              disabled={loadingStates.initialLoad}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              style={{ paddingLeft: '2.5rem', paddingRight: '2.5rem' }}
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={handleClearSearch}
                disabled={loadingStates.search}
              >
                <IconX size={16} />
              </Button>
            )}
            {loadingStates.search && (
              <div className="absolute right-10 top-1/2 -translate-y-1/2">
                <Loader2 className="animate-spin text-muted-foreground" size={16} />
              </div>
            )}
          </div>
          <Button 
            variant="outline" 
            onClick={openSpecialUsersModal}
            className="ml-auto shrink-0"
          >
            <IconUsers size={18} className="mr-2" />
            Special Organizations
          </Button>
        </div>

        {/* Table Card */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization Name</TableHead>
                  <TableHead>Total Users</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingStates.initialLoad || loadingStates.search ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-48" />
                          <Skeleton className="h-3 w-32" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-12" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="h-8 w-8 ml-auto" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : organizations.length > 0 ? (
                  organizations.map((org) => (
                    <TableRow key={org.user_name}>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="font-medium">{org.org_name}</span>
                          <span className="text-xs text-muted-foreground">
                            Email: {org.email || 'N/A'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                          <IconUsers size={12} />
                          {org.total_users}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" asChild>
                          <Link to={`/super-admin/organizations/${org.user_name}/details`}>
                            <IconEdit size={18} />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} className="h-64">
                      <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
                        <IconUsers size={48} className="text-muted-foreground" />
                        <p className="text-sm font-medium text-foreground">No organizations found</p>
                        <p className="text-xs text-muted-foreground">Try adjusting your search criteria or check back later</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {organizations.length > 0 && (
              <div className="flex items-center justify-between px-6 py-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {startItem}-{endItem} of {totalOrganizations} organizations
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loadingStates.search}
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <span className="text-sm text-muted-foreground px-2">
                    Page {currentPage} of {totalPages || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || loadingStates.search}
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Subscription Bypass Dialog */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {bypassAction ? 'Enable Subscription Bypass' : 'Disable Subscription Bypass'}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to {bypassAction ? 'enable' : 'disable'} subscription bypass for{' '}
              <strong>{selectedOrg?.org_name}</strong>?
            </DialogDescription>
          </DialogHeader>

          {bypassAction ? (
            <Alert>
              <AlertDescription>
                <p className="font-semibold mb-2">Please confirm:</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>This organization will bypass subscription limitations</li>
                  <li>Users will have access to premium features regardless of their subscription status</li>
                </ul>
              </AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <AlertDescription>
                <strong>Note:</strong> This organization will be subject to normal subscription rules and limitations.
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              disabled={loadingStates.processingByPassAction}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmBypass}
              disabled={loadingStates.processingByPassAction}
            >
              {loadingStates.processingByPassAction ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Processing...
                </>
              ) : (
                'Confirm'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Special Organizations Dialog */}
      <Dialog open={showSpecialUsersModal} onOpenChange={setShowSpecialUsersModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IconUsers size={20} />
              Manage Special Organizations
            </DialogTitle>
            <DialogDescription>
              Special organizations have elevated privileges regardless of subscription status
            </DialogDescription>
          </DialogHeader>

          {loadingStates.loadingSpecialUsers ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2 className="animate-spin text-primary" size={32} />
              <p className="text-sm text-muted-foreground">Loading special organizations...</p>
            </div>
          ) : (
            <Tabs defaultValue="list" className="flex-1 flex flex-col min-h-0">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="list">
                  Current Organizations ({specialUsers.length})
                </TabsTrigger>
                <TabsTrigger value="add">Add New</TabsTrigger>
              </TabsList>

              <TabsContent value="list" className="flex-1 overflow-hidden mt-4">
                {specialUsers.length === 0 ? (
                  <Alert>
                    <IconAlertCircle size={16} className="mt-0.5" />
                    <AlertDescription>
                      No special organizations configured yet.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <ScrollArea className="h-[400px] pr-4">
                    <div className="space-y-2">
                      {specialUsers.map((email) => (
                        <div
                          key={email}
                          className="flex items-center justify-between p-3 border rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                        >
                          <span className="text-sm font-medium">{email}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveSpecialUser(email)}
                            className="h-8 w-8"
                          >
                            <IconX size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </TabsContent>

              <TabsContent value="add" className="mt-4 space-y-4">
                <Formik
                  initialValues={{ email: '' }}
                  validationSchema={addSpecialUserSchema}
                  onSubmit={handleAddSpecialUser}
                >
                  {({ isSubmitting, errors, touched, dirty, isValid }) => (
                    <FormikForm className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">
                          Organization Email
                        </label>
                        <div className="flex gap-2">
                          <Field name="email">
                            {({ field }) => (
                              <Input
                                {...field}
                                type="email"
                                placeholder="Enter organization email"
                                className={errors.email && touched.email ? 'border-destructive' : ''}
                              />
                            )}
                          </Field>
                          <Button
                            type="submit"
                            disabled={!(isValid && dirty) || isSubmitting}
                          >
                            <IconUserPlus size={18} className="mr-2" />
                            Add
                          </Button>
                        </div>
                        <ErrorMessage
                          name="email"
                          component="p"
                          className="text-sm text-destructive"
                        />
                      </div>
                      <Alert>
                        <AlertDescription className="text-sm">
                          Added organizations will bypass all subscription checks and have full access to premium features.
                        </AlertDescription>
                      </Alert>
                    </FormikForm>
                  )}
                </Formik>
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowSpecialUsersModal(false)}
              disabled={loadingStates.savingSpecialUsers}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSpecialUsers}
              disabled={loadingStates.savingSpecialUsers || !hasChanges}
            >
              {loadingStates.savingSpecialUsers ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Organizations;
